import './autocomplete.scss';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _debounce from 'lodash/debounce';
import { MIN_VALUE_LENGTH } from './Constants';
import Foldout from './Foldout';
import { Input, Scrollbar, useTheme } from '..';
const Autocomplete = _ref => {
  let {
    children,
    menuHeight,
    menuPosition,
    nested,
    onChange,
    options,
    optionFlagKey,
    optionIconKey,
    optionSectionBodyKey,
    optionSectionKey,
    optionTitleKey,
    optionSubTitleKey,
    placeholder,
    sectionTitle,
    color,
    label,
    loading,
    value,
    itemSelect,
    name
  } = _ref;
  const {
    theme
  } = useTheme();
  const node = useRef();
  const dropDownMenu = useRef();
  const [listVisible, setListVisible] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  useEffect(() => setInternalValue(value), [value]); // keep state and props in sync

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleClickOutside = e => {
    if (node.current?.contains(e.target)) {
      return;
    }
    setListVisible(false);
  };
  const handleItem = item => {
    setInternalValue(item[optionTitleKey]);
    itemSelect(item);
    setListVisible(false);
  };
  const delayedQuery = useCallback(_debounce(query => onChange(query), 500), []);
  const handleOnChange = val => {
    setInternalValue(val);
    !val.length && delayedQuery(val);
    if (val.length > MIN_VALUE_LENGTH) {
      delayedQuery(val);
      setListVisible(true);
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: node,
    className: classNames('wrts-autocomplete', theme)
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    floatingLabel: true,
    onChange: handleOnChange,
    value: internalValue,
    color: color,
    label: label || placeholder,
    name: name
  }), /*#__PURE__*/React.createElement("div", {
    className: classNames('wrts-autocomplete-options', {
      'active ': listVisible && (!!options.length || loading),
      'nested-content': nested,
      'position-left': menuPosition === 'left',
      'position-right': menuPosition === 'right'
    }),
    onClick: () => setListVisible(false)
  }, /*#__PURE__*/React.createElement(Scrollbar, {
    autoHeightMax: menuHeight,
    thumbColor: theme === 'dark' ? '#240E3E' : undefined
  }, /*#__PURE__*/React.createElement("div", {
    ref: dropDownMenu,
    className: classNames('body', loading)
  }, /*#__PURE__*/React.createElement("span", {
    className: "text section-title"
  }, sectionTitle), children || /*#__PURE__*/React.createElement(Foldout, {
    nested: nested,
    options: options,
    optionSectionKey: optionSectionKey,
    optionSectionBodyKey: optionSectionBodyKey,
    optionTitleKey: optionTitleKey,
    optionSubTitleKey: optionSubTitleKey,
    optionIconKey: optionIconKey,
    optionFlagKey: optionFlagKey,
    handleItem: handleItem,
    loading: loading
  })))));
};
Autocomplete.defaultProps = {
  nested: false,
  menuHeight: '100vh',
  menuPosition: 'right',
  onChange: () => {},
  itemSelect: () => {},
  options: [],
  optionIconKey: 'icon',
  optionFlagKey: 'flag',
  optionSectionBodyKey: 'body',
  optionSectionKey: 'section',
  optionTitleKey: 'title',
  optionSubTitleKey: 'code',
  sectionTitle: '',
  placeholder: ''
};
Autocomplete.propTypes = {
  children: PropTypes.node,
  menuHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  menuPosition: PropTypes.oneOf(['right', 'left']),
  nested: PropTypes.bool,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  optionFlagKey: PropTypes.string,
  optionIconKey: PropTypes.string,
  optionSectionBodyKey: PropTypes.string,
  optionSectionKey: PropTypes.string,
  optionTitleKey: PropTypes.string,
  optionSubTitleKey: PropTypes.string,
  placeholder: PropTypes.string
};
export default Autocomplete;